import {
  Activate_Payment_Method,
  Constraint_Purchase_Threshold_Counting_Attribute,
  ConstraintConfigurationInput,
  ConstraintRequiresTransactionVoucherInput,
  Ownership_Condition,
  Payment_Instrument_Evaluation_Method,
  Payment_Instrument_Type,
  Payment_Method_Evaluation_Method,
  Store_Location_Evaluation_Method,
  Weekday,
} from '@occc/graphql';
import { formatTime } from '@occc-app/lib/format';
import {
  CreateConstraintOperationType,
  makeCreateConstraint,
} from '@occc-app/offerHelpers/operations/createConstraintOperation.utils';
import {
  makeUpdateConstraint,
  UpdateConstraintOperationType,
} from './updateConstraintOperation.utils';

export const makeCreateInStatesConstraintOperation = (
  offerId: string,
  states: string[],
): CreateConstraintOperationType => {
  const configuration: ConstraintConfigurationInput = {
    locationInState: {
      states,
    },
  };

  return makeCreateConstraint(offerId, configuration);
};

export const makeCreateInAudienceConstraintOperation = (
  offerId: string,
  audienceId: string,
): CreateConstraintOperationType => {
  const configuration: ConstraintConfigurationInput = {
    inAudience: {
      audienceId,
    },
  };

  return makeCreateConstraint(offerId, configuration);
};

export const makeCreateSubBrandConstraintOperation = (
  offerId: string,
  subBrandUuids: string[],
): CreateConstraintOperationType => {
  const configuration: ConstraintConfigurationInput = {
    subBrand: {
      subBrandUuids,
    },
  };

  return makeCreateConstraint(offerId, configuration);
};

export const makeUpdateSubBrandConstraintOperation = (
  constraintId: string,
  subBrandUuids: string[],
): UpdateConstraintOperationType => {
  const configuration: ConstraintConfigurationInput = {
    subBrand: {
      subBrandUuids,
    },
  };
  return makeUpdateConstraint(constraintId, configuration);
};

export const makeCreateDaysOfWeekConstraintOperation = (
  offerId: string,
  availableWeeklyOnDays: Weekday[],
  startOfDayTimezone: string,
): CreateConstraintOperationType => {
  const configuration: ConstraintConfigurationInput = {
    daysOfWeek: {
      availableWeeklyOnDays: availableWeeklyOnDays,
      startOfDayTimezone,
    },
  };

  return makeCreateConstraint(offerId, configuration);
};

export const makeCreateTimeOfDayConstrainOperation = (
  offerId: string,
  availableDailyStartingAtTime: string | Date,
  availableDailyEndingAtTime: string | Date,
  timezone: string,
): CreateConstraintOperationType => {
  const configuration: ConstraintConfigurationInput = {
    timeOfDay: {
      timezone,
      availableDailyStartingAtTime: formatTime(availableDailyStartingAtTime),
      availableDailyEndingAtTime: formatTime(availableDailyEndingAtTime),
    },
  };

  return makeCreateConstraint(offerId, configuration);
};

export const makeCreateRequiresVoucherConstraintOperation = (
  offerId: string,
): CreateConstraintOperationType => {
  const configuration: ConstraintConfigurationInput = {
    isARequiresVoucherConstraint: true,
  };

  return makeCreateConstraint(offerId, configuration);
};

export const makeCreateRequiresVoucherAvailableForDiscountConstraintOperation = (
  offerId: string,
): CreateConstraintOperationType => {
  const configuration: ConstraintConfigurationInput = {
    isARequiresVoucherAvailableForDiscountConstraint: true,
  };

  return makeCreateConstraint(offerId, configuration);
};

export const makeCreateRequiresTransactionVoucherConstraintOperation = (
  offerId: string,
  params: ConstraintRequiresTransactionVoucherInput,
): CreateConstraintOperationType => {
  const configuration: ConstraintConfigurationInput = {
    requiresTransactionVoucherConstraintWithConfiguration: params,
  };

  return makeCreateConstraint(offerId, configuration);
};

export const makePreAuthConstraintOperation = (offerId: string): CreateConstraintOperationType => {
  const configuration: ConstraintConfigurationInput = {
    isAPreAuthConstraint: true,
  };

  return makeCreateConstraint(offerId, configuration);
};

export const makePurchaseConstraintOperation = (
  offerId: string,
  eligibleProductGroupIds: string[],
  countProductsBy: Constraint_Purchase_Threshold_Counting_Attribute,
  threshold: number,
): CreateConstraintOperationType => {
  const configuration: ConstraintConfigurationInput = {
    purchaseThreshold: {
      eligibleProductGroupIds,
      countProductsBy,
      threshold,
    },
  };

  return makeCreateConstraint(offerId, configuration);
};

export const makeCreateStoreLocationConstraintOperation = (
  offerId: string,
  evaluationMethod: Store_Location_Evaluation_Method,
  locationIds: string[],
): CreateConstraintOperationType => ({
  name: 'constraintCreate',
  variables: {
    input: {
      offerId,
      configuration: {
        storeLocation: {
          evaluationMethod,
          locationIds,
        },
      },
    },
  },
});

export const makeCreateStoreLocationGroupConstraintOperation = (
  offerId: string,
  locationGroupIds: string[],
): CreateConstraintOperationType => ({
  name: 'constraintCreate',
  variables: {
    input: {
      offerId,
      configuration: {
        storeLocationGroup: {
          locationGroupIds,
        },
      },
    },
  },
});

export const makeCreatePaymentMethodConstraintOperation = (
  offerId: string,
  evaluationMethod: Payment_Method_Evaluation_Method,
  paymentMethods: Array<Activate_Payment_Method>,
): CreateConstraintOperationType => ({
  name: 'constraintCreate',
  variables: {
    input: {
      offerId,
      configuration: {
        paymentMethod: {
          evaluationMethod,
          paymentMethods,
        },
      },
    },
  },
});

export const makeCreatePaymentInstrumentConstraintOperation = (
  offerId: string,
  evaluationMethod: Payment_Instrument_Evaluation_Method,
  paymentInstruments: Array<Payment_Instrument_Type>,
): CreateConstraintOperationType => ({
  name: 'constraintCreate',
  variables: {
    input: {
      offerId,
      configuration: {
        paymentInstrument: {
          evaluationMethod,
          paymentInstruments,
        },
      },
    },
  },
});

export const makeCreatePurchasableEligibilityConstraintOperation = (
  offerId: string,
  awardIds: string[],
  ownershipCondition: Ownership_Condition,
): CreateConstraintOperationType => ({
  name: 'constraintCreate',
  variables: {
    input: {
      offerId,
      configuration: {
        purchasableEligibility: {
          voucheredOfferIds: awardIds,
          ownershipCondition,
        },
      },
    },
  },
});

export const makeCreateEmailRequiredConstraintOperation = (
  offerId: string,
): CreateConstraintOperationType => ({
  name: 'constraintCreate',
  variables: {
    input: {
      offerId,
      configuration: {
        isEmailRequired: true,
      },
    },
  },
});

export const makeCreateInStoreOnlyConstraintOperation = (
  offerId: string,
): CreateConstraintOperationType => ({
  name: 'constraintCreate',
  variables: {
    input: {
      offerId,
      configuration: {
        isInStoreOnlyConstraint: true,
      },
    },
  },
});

export const makeCreateOrderAheadOnlyConstraintOperation = (
  offerId: string,
): CreateConstraintOperationType => ({
  name: 'constraintCreate',
  variables: {
    input: {
      offerId,
      configuration: {
        isOrderAheadOnlyConstraint: true,
      },
    },
  },
});
